import * as contracts from "../contracts/contracts";
import ActivityType from "./activityType";
import ActivityWindow from "./activityWindow";
import { ActivityStatus } from "../contracts/activityStatus";
import ActivityEntity from "./activityEntity";
import { WindowDefinition } from "./windowDefinition";
import { ActivityAGM } from "../core/activityAGM";
import { CloneOptions } from "./cloneOptions";
import { AttachedActivityDescriptor } from "./attachedActivityDescriptor";
import { nodeify } from "../helpers/promiseExtensions";
import { ifNotUndefined } from "../helpers/util";

/**
 * Activity is an instance of an activity type just like an object is an instance of a class
 * in class-based languages (and just like ‘John’ is an instance of a 'Person’).
 *
 * Activity is often used as a synonym for activity instance.
 *
 * Each activity instance has a single owner window and can optionally have one or more helper windows.
 *
 * @module activity
 */
export default class Activity extends ActivityEntity {

  public _id: string;
  public _actType: string;
  public _status: ActivityStatus;
  public _context: object;
  public _ownerId: string;
  public _agm: ActivityAGM;
  public _attached: any;

  constructor(
    id: string,
    actType: string,
    status: ActivityStatus,
    context: object,
    ownerId: string) {
    super(id);
    this._id = id;
    this._actType = actType;
    this._status = status;
    this._context = context;
    this._ownerId = ownerId;
    this._agm = new ActivityAGM(this);
  }

  /**
   * Activity type
   * @var {module:activityType} type
   */
  public get type(): ActivityType {
    if (this._manager) {
      return this._manager.getActivityType(this._actType);
    }
    return undefined;
  }

  /**
   * Get current activity context. To update use updateContext , to replace setContext
   * @var {object} context
   */
  public get context(): object {
    return this._context;
  }

  /**
   * Get activity status
   * @var {activityStatus} status
   */
  public get status(): ActivityStatus {
    return this._status;
  }

  /**
   * The owner window
   * @var {module:activityWindow} owner
   */
  public get owner(): ActivityWindow {
    if (!this._ownerId) {
      return null;
    }
    return this._manager.getWindows({ id: this._ownerId })[0];
  }

  /**
   * All windows participating in the activity
   * @var {module:activityWindow[]} windows
   */
  public get windows(): ActivityWindow[] {
    return this._manager.getWindows({ activityId: this._id });
  }

  /**
   * @var {module:activityAGM} agm
   */
  public get agm(): ActivityAGM {
    return this._agm;
  }

  /**
   * Joins a specific window to the activity
   *
   * @private
   * @function join
   * @param window    The window to join
   * @param callback  (Optional) Result callback
   * @returns         Promise for ActivityWindow
   */
  public addWindow(window: ActivityWindow, callback?: contracts.Callback<ActivityWindow>): Promise<ActivityWindow> {
    return this._manager.addWindowToActivity(this, window, callback);
  }

  /**
   * Creates a new window and joins it to the activity
   *
   * @function createWindow
   * @param {string|windowDefinition} windowType The type of the window to be created
   * @param callback
   * @returns {Promise<module:activityWindow>}
   */
  public createWindow(windowType: string | WindowDefinition, callback?: contracts.Callback<ActivityWindow>): Promise<ActivityWindow> {
    return this._manager.createWindow(this, windowType, callback);
  }

  /**
   * Creates a stacked set of windows and joins them to the
   *
   * @function createStackedWindows
   * @param {string[] | windowDefinition[]} windowTypes The types of the windows to be created
   * @param {number} timeout
   * @param callback
   * @returns {Promise<module:activityWindow[]>}
   */
  public createStackedWindows(windowTypes: (string | WindowDefinition)[], timeout?: number, callback?: contracts.Callback<ActivityWindow[]>): Promise<ActivityWindow[]> {
    return this._manager.createStackedWindows(this, windowTypes, timeout, callback);
  }

  /**
   * Removes a window from the activity
   *
   * @private
   * @param window
   * @param callback
   * @returns     Promise for ActivityWindow
   */
  public leave(window: ActivityWindow, callback?: contracts.Callback<ActivityWindow>): Promise<ActivityWindow> {
    return this._manager.leaveWindowFromActivity(this, window, callback);
  }

  /**
   * Get all windows participating in the activity from a given type
   *
   * @param {string} windowType
   * @returns {module:activityWindow[]}
   */
  public getWindowsByType(windowType: string): ActivityWindow[] {
    const filter = { activityId: this._id, type: windowType };
    return this._manager.getWindows(filter);
  }

  /**
   * Replaces the activity context with a new one.
   *
   * @function setContext
   * @param {object} context   The new context
   * @param callback
   * @returns {Promise<Object>}
   */
  public setContext(context: object, callback?: contracts.Callback<Activity>): Promise<object> {
    return this._manager.setActivityContext(this, context, callback);
  }

  /**
   * Updates activity context using the properties from the context argument.
   * If old context is {a:1, b:2, c:3} and invoking updateContext({b:3, c:null}) will result a context
   * be {a:1, b:3}
   *
   * @function updateContext
   * @param {object} context
   * @param callback
   * @return {Promise<object>}
   */
  public updateContext(context: object, callback?: contracts.Callback<Activity>): Promise<object> {
    return this._manager.updateActivityContext(this, context, callback);
  }

  /**
   * Subscribe for status change events
   *
   * @function onStatusChange
   *
   * @param {activityStatusChangeHandler} handler Handler function that will receive status notifications
   */
  public onStatusChange(handler: (activity: Activity, newStatus: ActivityStatus, oldStatus: ActivityStatus) => void): () => void {
    return this._manager.subscribeActivityEvents((a, ns, os) => {
      if (a.id === this.id) {
        handler(a, ns, os);
      }
    });
  }

  /**
   * Subscribe for window related events, like joined, removed from activity
   *
   * @function onWindowEvent
   * @param {windowEventHandler} handler Handler function that will receive windows events
   */
  public onWindowEvent(handler: contracts.ActivityWindowEventHandler): () => void {
    return this._manager.subscribeWindowEvents((a, w, e) => {
      if (a.id === this.id) {
        handler(a, w, e);
      }
    });
  }

  /**
   * Subscribe for context updates
   *
   * @function onContextChanged
   * @param {contextChangedHandler} handler Handler function that will receive context related events
   */
  public onContextChanged(handler: contracts.ContextUpdateHandler) {
    this._manager.subscribeActivityContextChanged((act, context, updated, removed) => {
      if (act.id === this.id) {
        handler(context, updated, removed, act);
      }
    });

    try {
      // replay the context at the time of subscribing, swallow user exceptions
      handler(this.context, this.context, [], this);
    } catch (e) {
      return;
    }
  }

  /**
   * Stops the activity
   * @function stop
   */
  public stop() {
    this._manager.stopActivity(this);
  }

  /**
   * Clones the activity into a new one
   * @function clone
   * @param {CloneOptions} options
   */
  public clone(options: CloneOptions) {
    return this._manager.clone(this, options);
  }

  public attach(activity: Activity | string, tag?: object): Promise<AttachedActivityDescriptor> {
    let activityId;
    if (typeof activity === "string") {
      activityId = activity;
    } else {
      activityId = activity.id;
    }
    return this._manager.attachActivities(activityId, this.id, tag);
  }

  public onActivityAttached(callback: (descriptor: AttachedActivityDescriptor) => void) {
    this._manager.subscribeActivitiesAttached((newActId: string, oldActId: string, descriptor: AttachedActivityDescriptor) => {
      if (newActId !== this._id) {
        return;
      }

      callback(descriptor);
    });
  }

  public onDetached(callback: (newAct: Activity, state: any) => void) {
    this._manager.subscribeActivitiesDetached((newAct: Activity, originalActivity: Activity, state: any) => {
      if (originalActivity.id !== this._id) {
        return;
      }

      callback(newAct, state);
    });
  }

  public _updateCore(other: Activity) {
    super._updateCore(other);
    ifNotUndefined(other._actType, (x) => this._actType = x);
    ifNotUndefined(other._context, (x) => this._context = x);
    ifNotUndefined(other._ownerId, (x) => this._ownerId = x);
    if (other._status && (!this._status || (this._status.state !== other._status.state))) {
      this._status = other._status;
    }
  }

  public _updateDescriptors(allStates: any) {
    this._attached = allStates.map((s: any) => {
      return new AttachedActivityDescriptor(this._manager, this._id, s);
    });
  }

  public get attached(): AttachedActivityDescriptor[] {
    return this._attached;
  }

  public setFrameColor(color: string, callback: () => void): Promise<Activity> {
    const promise = new Promise<Activity>((resolve, reject) => {

      let callbacksToWait = this.windows.length;
      if (callbacksToWait === 0) {
        resolve(this);
      }

      this.windows.forEach((w) => {
        w.underlyingWindow.setFrameColor(color, () => {
          callbacksToWait--;
          if (callbacksToWait <= 0) {
            resolve(this);
          }
        });
      });

      setTimeout(() => {

        if (callbacksToWait > 0) {
          reject(this.id + " - timed out waiting for setFrameColor with" + color);
        }

      }, 5000);

    });

    return nodeify(promise, callback);
  }

  public getFrameColor(): string {

    if (!this.windows || this.windows.length === 0) {
      return "";
    }

    return this.windows[0].underlyingWindow.frameColor;
  }
}
